import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { MarkdownProps } from "./Markdown"
import { MouseEvent } from 'react'
import Debouncer from "reusable/utils/Debouncer"
import { postgresApiClient } from "api/trpc-pg/trpc-client"
import Debug from "reusable/utils/debug"
import * as Sentry from "@sentry/react"
import { makeAutoObservable, runInAction } from "mobx"
import { snackbarError } from "reusable/mui/snackbar/snackbar"
import { preserveBlankLines } from "@gap/utils/markdown/markdown-blank-lines"
import { MARKDOWN_LOADING_CLASS, MarkdownLoaderState } from "./MarkdownLoaderStatus"
import { isAdvisorEdit } from "./editorChange"
import type { MDXEditorMethods } from "@mdxeditor/editor"
import type MarkdownType from "@gap/backend/app-services/markdown/models/MarkdownType"
import type { MarkdownSaveSource } from "./MarkdownSaveTracker"

interface MarkdownStoreProps extends MarkdownProps { }

export interface DirectiveInsert {
   name: string
   type: 'textDirective' | 'leafDirective' | 'containerDirective'
   attributes?: Record<string, string>
}

export default class MarkdownStore implements MarkdownSaveSource {
   sp
   /** Tracks the last markdown string emitted by the editor's onChange, used to prevent setMarkdown feedback loops */
   lastEditorMarkdown: string | undefined = undefined
   /** Whether an edit is waiting on the autosave debounce. */
   hasPendingSave = false
   /** Whether the most recent autosave failed. */
   lastSaveFailed = false
   private previousMarkdown: string | undefined
   private autoSaveDebouncer = new Debouncer({ debounce: 1500 })
   /** Set just before a programmatic `setMarkdown`, whose `onChange` is indistinguishable from a keystroke. */
   private ignoreNextChange = false
   /** Bound to the live editor by the directive insertion bridge. */
   private directiveInserter: ((directive: DirectiveInsert) => void) | null = null
   private inFlightSaves = new Set<Promise<void>>()
   /** The latest save that failed, re-sent by the next flush unless a newer edit replaces it. */
   private failedSave: { markdown: string, markdownId: string, type: MarkdownType } | null = null

   constructor(sp: MarkdownStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
      this.previousMarkdown = sp.markdown
   }

   /** Whether an edit is waiting to be saved or being saved. */
   get isSaving() {
      return this.hasPendingSave || this.inFlightSaves.size > 0
   }

   get toolbarClass() {
      return this.sp.readOnly ? 'toolbar-hidden' : 'toolbar-visible'
   }

   /** Whether an agent is working, which the bar, the editor lock and the feather fade all read. */
   get loaderState(): MarkdownLoaderState {
      return this.sp.loaderState ?? 'idle'
   }

   /** The placeholder, suppressed while generating since the editor is locked and about to fill. */
   get placeholder(): string | undefined {
      return this.loaderState === 'loading' ? undefined : this.sp.placeholder
   }

   /** Whether the editor rejects input, which generating does as well as the `readOnly` prop. */
   get isReadOnly() {
      return !!this.sp.readOnly || this.loaderState === 'loading'
   }

   get contentClass() {
      return this.loaderState === 'loading' ? `${this.toolbarClass} ${MARKDOWN_LOADING_CLASS}` : this.toolbarClass
   }

   handleError = (payload: { error: string; source: string }) => {
      Debug.log.markdown('MDXEditor error:', payload)
      snackbarError('Unable to load preview. Please contact support if this persists.')
      Sentry.captureException(new Error(payload.error), {
         tags: {
            component: 'MarkdownStore',
            action: 'MDXEditor.onError',
            source: payload.source,
            error: payload.error,
            markdownId: this.sp.id,
            markdownType: JSON.parse(JSON.stringify(this.sp.type)),
         }
      })
   }

   /** Saves a change still waiting on the autosave debounce and waits for every save in flight. */
   flushAutoSave = async () => {
      await this.flushPendingSave()
   }

   setDirectiveInserter = (inserter: (directive: DirectiveInsert) => void) => {
      this.directiveInserter = inserter
   }

   /** Inserts a directive at the caret through the editor bridge; used by the upload dialogs outside the toolbar. */
   insertDirective = (directive: DirectiveInsert) => {
      this.directiveInserter?.(directive)
   }

   handleLinkClick = (e: MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement
      const link = target.closest('a')
      if (link && link.href) {
         e.preventDefault()
         window.open(link.href, '_blank', 'noopener,noreferrer')
      }
   }

   /**
    * Writes markdown the user didn't type, without the write counting as an edit.
    * @param params The editor to write to, and the markdown to write.
    */
   applyExternalMarkdown = (params: { editor: MDXEditorMethods, markdown: string }) => {
      const { editor, markdown } = params
      // MDXEditor skips a trim-equal setMarkdown, so there is nothing to write or to guard.
      if (markdown.trim() === editor.getMarkdown().trim()) return

      this.ignoreNextChange = true
      editor.setMarkdown(markdown)
      // MDXEditor mutes the change this write emits, so nothing downstream can close the flag.
      this.ignoreNextChange = false
      this.previousMarkdown = markdown
   }

   /**
    * Wrapper on the onChange. Detect if a GapFile directive have been remove, and ifso, mark the file for deletion
    * @param editorMarkdown The editor's markdown after the change.
    * @param isInitialNormalize MDXEditor's flag for the change its own initial import emits.
    */
   handleChange = (editorMarkdown: string, isInitialNormalize = false) => {
      // The editor emits an empty paragraph as a bare run of blank lines, which markdown collapses on re-parse —
      // so a typed return key would vanish on reload. Store it as an explicit blank-line paragraph instead.
      const newMarkdown = preserveBlankLines(editorMarkdown)
      this.lastEditorMarkdown = newMarkdown
      if (!isAdvisorEdit({ isProgrammatic: this.ignoreNextChange, isInitialNormalize })) {
         // Not the advisor's, but `previousMarkdown` moves on so the next real edit diffs against it.
         this.ignoreNextChange = false
         this.previousMarkdown = newMarkdown
         return
      }
      const previousFiles = this.extractGapFileNames(this.previousMarkdown)
      const currentFiles = this.extractGapFileNames(newMarkdown)

      // Find files that were removed
      for (const fileName of previousFiles) {
         if (!currentFiles.has(fileName)) {
            this.markFileForDeletion(fileName)
         }
      }

      this.previousMarkdown = newMarkdown
      this.sp.onChange?.(newMarkdown)

      if (this.sp.autoSave) {
         const saveParams = { markdown: newMarkdown, markdownId: this.sp.id, type: this.sp.type }
         this.hasPendingSave = true
         this.autoSaveDebouncer.run(() => this.trackSave(this.autoSave(saveParams)))
      }
   }

   /**
    * Runs the edit waiting on the debounce now, re-sends a save that failed before this flush, and waits for every save in flight.
    * @returns False when the latest save failed.
    */
   flushPendingSave = async (): Promise<boolean> => {
      const earlierFailedSave = this.failedSave
      await this.autoSaveDebouncer.flush()
      await Promise.all([...this.inFlightSaves])
      if (earlierFailedSave && this.failedSave === earlierFailedSave) {
         await this.trackSave(this.autoSave(earlierFailedSave))
      }
      return !this.lastSaveFailed
   }

   private trackSave = async (save: Promise<void>) => {
      this.inFlightSaves.add(save)
      try {
         await save
      } finally {
         runInAction(() => this.inFlightSaves.delete(save))
      }
   }

   private autoSave = async (params: { markdown: string, markdownId: string, type: MarkdownType }) => {
      const { markdown, markdownId, type } = params
      this.hasPendingSave = false
      // Re-checked here, not where the save is scheduled: the debounce outlasts the start of a generation.
      if (this.loaderState === 'loading') return
      try {
         if (type.type === 'blueprint') {
            await postgresApiClient.markdown.saveBlueprintMarkdown.mutate({ blueprintId: type.blueprintId, markdown })
         } else if (type.type === 'blueprint-notes') {
            await postgresApiClient.markdown.saveBlueprintNotes.mutate({ blueprintId: type.blueprintId, markdown })
         } else {
            await postgresApiClient.markdown.saveMarkdown.mutate({ markdown, id: markdownId, type })
         }
         runInAction(() => {
            this.lastSaveFailed = false
            this.failedSave = null
         })
      } catch (error: any) {
         runInAction(() => {
            this.lastSaveFailed = true
            this.failedSave = params
         })
         Debug.log.markdown('Auto-save failed:', error)
         Sentry.captureException(error, { tags: { component: 'MarkdownStore', action: 'autoSave' }, })
      }
   }

   /** Extract all GapFile and GapImage rawFileName values from markdown */
   private extractGapFileNames(markdown: string | undefined): Set<string> {
      if (!markdown) return new Set()
      const regex = /::?Gap(?:File|Image)\{[^}]*rawFileName="([^"]+)"[^}]*\}/g
      const fileNames = new Set<string>()
      let match
      while ((match = regex.exec(markdown)) !== null) {
         fileNames.add(match[1])
      }
      return fileNames
   }

   // When a file gets removed in markdown, we need to update the entity and mark the file for deletion
   // Cronjob will later take care of the deletion of the file from s3(unless the user pressed undo)
   private markFileForDeletion = async (rawFileName: string) => {
      try {
         await postgresApiClient.markdown.markFileForDeletion.mutate({ fileName: rawFileName, id: this.sp.id, type: this.sp.type })
      } catch (error) {
         Debug.log.markdown('Unable to mark file for deletion:', rawFileName, error)
      }
   }
}

export const { Provider: MarkdownStoreProvider, useProvidedStore: useMarkdownStore } = createStoreContext(MarkdownStore)
